import React, { useMemo } from "react";
import moment from "moment";
import {
  buildMonthGrid,
  formatHm,
  formatPunch,
  statusLabel,
  statusPillClass,
  todayArrivalLabel,
  workLocationLabel,
} from "./attendanceUiHelpers";

const AttendanceOverviewPanel = ({
  monthMoment,
  onPrevMonth,
  onNextMonth,
  analyticsDaily,
  analyticsLoading,
  shift,
  /** When false (e.g. self-service employees), hide stat row; calendar stays. */
  showStatCards = true,
  /** "average" = month averages; "today" = selected employee's times for today (Team tab). */
  statCardMode = "average",
}) => {
  const daysByDate = useMemo(() => {
    const m = {};
    (analyticsDaily?.days || []).forEach((d) => {
      m[d.date] = d;
    });
    return m;
  }, [analyticsDaily]);

  const weeks = useMemo(() => buildMonthGrid(monthMoment), [monthMoment]);
  const stats = analyticsDaily?.stats;
  const todayStr = moment().format("YYYY-MM-DD");
  const todayRecord = (analyticsDaily?.days || []).find((d) => d.date === todayStr);

  const cards =
    statCardMode === "today"
      ? [
          {
            title: "Punch-In",
            sub: "Today's entry time",
            val: formatPunch(todayRecord?.punchIn),
            trend: null,
          },
          {
            title: "Punch-Out",
            sub: "Today's exit time",
            val: formatPunch(todayRecord?.punchOut),
            trend: null,
          },
          {
            title: "Worked Hours",
            sub: "Time worked today",
            val: todayRecord?.workedMinutes != null ? formatHm(todayRecord.workedMinutes) : "—",
            trend: null,
          },
          {
            title: "Arrival",
            sub: "Today's punctuality",
            val: todayRecord ? todayArrivalLabel(todayRecord.status) : "—",
            trend: null,
          },
        ]
      : [
          { title: "Avg Punch-In", sub: "Average entry time", val: stats?.avgPunchIn?.label || "—", trend: null },
          { title: "Avg Punch-Out", sub: "Average exit time", val: stats?.avgPunchOut?.label || "—", trend: null },
          { title: "Avg Worked Hours", sub: "Time worked daily on average", val: stats?.avgWorkedLabel || "—", trend: null },
          {
            title: "On-Time Arrival %",
            sub: "Percentage of punctual days",
            val: stats?.onTimePercent != null ? `${stats.onTimePercent}%` : "—",
            trend: null,
          },
        ];

  return (
    <div className="att-overview">
      {showStatCards ? (
        <div className="att-stats-grid">
          {cards.map((c) => (
            <div key={c.title} className="att-stat-card card-border card-body">
              <div className="att-stat-card__title">{c.title}</div>
              <div className="att-stat-card__value">{analyticsLoading ? "…" : c.val}</div>
              <div className="att-stat-card__sub">{c.sub}</div>
            </div>
          ))}
        </div>
      ) : null}

      <div className={`att-cal-toolbar card-border card-body ${showStatCards ? "mt-4" : ""}`}>
        <div className="att-cal-toolbar__row">
          <button type="button" className="button px-3 py-1" onClick={onPrevMonth}>
            ‹
          </button>
          <span className="att-cal-toolbar__title">{monthMoment.format("MMMM YYYY")}</span>
          <button type="button" className="button px-3 py-1" onClick={onNextMonth}>
            ›
          </button>
        </div>
        {shift?.label && (
          <div className="att-shift-badge">
            {shift.label}: {shift.start} – {shift.end}
          </div>
        )}
      </div>

      <div className="att-month-grid card-border card-body mt-4">
        <div className="att-month-grid__head">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="att-month-grid__head-cell">
              {d}
            </div>
          ))}
        </div>
        {weeks.map((week, wi) => (
          <div key={wi} className="att-month-grid__week">
            {week.map((day) => {
              const ds = day.format("YYYY-MM-DD");
              const inMonth = day.month() === monthMoment.month();
              const rec = daysByDate[ds];
              const isToday = ds === todayStr;
              const isFutureDay = ds > todayStr;
              const showStatusBadge = inMonth && rec && !isFutureDay;
              return (
                <div
                  key={ds}
                  className={`att-month-grid__cell${!inMonth ? " att-month-grid__cell--muted" : ""}${
                    isToday ? " att-month-grid__cell--today" : ""
                  }`}
                >
                  <div className="att-month-grid__date">{day.format("D")}</div>
                  {showStatusBadge ? (
                    <div className="att-month-grid__badges">
                      <span className={statusPillClass(rec.status)}>{statusLabel(rec.status)}</span>
                    </div>
                  ) : null}
                  {showStatusBadge &&
                  (rec.workLocation === "wfh" ||
                    rec.workLocation === "wfs" ||
                    rec.workLocation === "wfo") ? (
                    <div className="att-month-grid__wl">{workLocationLabel(rec.workLocation)}</div>
                  ) : null}
                  {inMonth && rec?.status === "in_progress" && !isFutureDay ? (
                    <div className="att-month-grid__shift">General Shift</div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AttendanceOverviewPanel;
